import React, { useContext, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';

import useApi from 'common/useApi';
import UiContext from 'common/UiContext/UiContext';
import AuthContext from 'common/auth';
import getHashrateValueWithUnit from 'common/getHashrateValueWithUnit';
import {
  RANGES,
  RANGE_CONFIG,
  RANGE_LABELS,
  HashrateRange,
  buildTimeTicks,
} from 'common/timeAxis';

import HashrateGraph from 'components/HashrateGraph';

import './PoolHashrateHistory.scss';

type PoolPoint = { date: string; hashrate: string };

// Pool-wide effective-hashrate history. Reuses the shared HashrateGraph with a
// single visible series (the pool's effective hashrate), mirroring the per-worker
// chart's range selector and axis scaffolding.
const PoolHashrateHistory = () => {
  const { addToast } = useContext(UiContext);
  const { user } = useContext(AuthContext)!;
  const timezone = user?.timezone;

  const [range, setRange] = useState<HashrateRange>('24h');

  const { data, isError, isLoading, isFetching } = useApi(
    'getList',
    '/api/dashboard/pool-hashrates',
    { range },
  );

  useEffect(() => {
    if (isError) addToast!('Error loading pool hashrate history');
  }, [isError]);

  // HashrateGraph plots three series; we feed only the `hashrate` series and
  // pad the rest so a single line renders.
  const hashrates = useMemo(
    () => (data ?? []).map((p: PoolPoint) => ({
      date: p.date,
      hashrate: p.hashrate,
      scoring_hashrate: '0',
      workers_active: 0,
    })),
    [data],
  );

  const { startMs, endMs, bucketMs } = useMemo(() => {
    const end = Date.now();
    const cfg = RANGE_CONFIG[range];

    return { startMs: end - cfg.windowMs, endMs: end, bucketMs: cfg.bucketMs };
  }, [range, data]);

  const ticks = useMemo(
    () => buildTimeTicks(startMs, endMs, range, timezone),
    [startMs, endMs, range, timezone],
  );

  const hashrateUnits = useMemo(() => {
    const values = hashrates.map((p: PoolPoint) => parseFloat(p.hashrate));

    return getHashrateValueWithUnit(Math.max(1, ...values));
  }, [hashrates]);

  const max = useMemo(() => {
    let m = 1;

    hashrates.forEach((p: PoolPoint) => {
      if (parseFloat(p.hashrate) > m) m = parseFloat(p.hashrate);
    });
    const mag = Math.pow(10, Math.floor(Math.log10(m)));

    return Math.ceil(m / mag) * mag;
  }, [hashrates]);

  const steps = useMemo(() => {
    const s = parseInt(max.toString()[0]);

    return isNaN(s) || s === 0 ? 1 : s;
  }, [max]);

  const hashrateLabel = (i: number) => {
    const val = (max / steps) * i;
    const u = getHashrateValueWithUnit(val);

    return val / u.coefficient + u.unit;
  };

  return (
    <div className={classNames({ box: true, isLoading: isLoading || isFetching, 'pool-hashrate-history': true })}>
      <h5>Pool Hashrate History</h5>
      <nav className='boxNavigation'>
        <div className='rangeSelector'>
          {RANGES.map((opt) => (
            <button
              key={opt}
              type='button'
              className={classNames({ active: opt === range })}
              onClick={() => setRange(opt)}
            >
              {RANGE_LABELS[opt]}
            </button>
          ))}
        </div>
      </nav>
      {!isLoading && !isFetching && hashrates.length === 0 && (
        <p className='pool-hashrate-empty'>
          Collecting data — the pool hashrate history will appear as snapshots accumulate.
        </p>
      )}
      <div className='hashrate-graph-scaffolding'>
        <div className='unit-names'>
          <span>Hashrate</span>
          <span />
        </div>
        <div className='hashrate-values'>
          {hashrates.length > 0 && steps > 0 && (
            [...Array(steps + 1).fill(true)].map((_, k) => (
              <span key={k}>{hashrateLabel(k)}</span>
            ))
          )}
        </div>
        <HashrateGraph
          isLoading={isLoading || isFetching}
          hashrateMaximized={true}
          hashRatesSlice={hashrates}
          hashrateUnits={hashrateUnits}
          maxWorkers={0}
          min={0}
          max={max}
          graphVisibleElements={['hashrate']}
          startMs={startMs}
          endMs={endMs}
          bucketMs={bucketMs}
          ticks={ticks}
          timezone={timezone}
        />
        <div className='count-values' />
        <div className='hours'>
          {ticks.map((tick, k) => (
            <span key={k} style={{ left: `${tick.fraction * 100}%` }}>{tick.label}</span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PoolHashrateHistory;
